import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { StepIcon } from "../StepIcon";
import { countByReason, formatTime, prepareNotExecuted } from "../executionTimeline";

// Los pasos que no corrieron, fuera del eje de tiempo. `decided_at` es un
// instante de decisión, no el fin de un intervalo: acá se muestra como hora,
// nunca como duración.

const REASON_BADGE = {
  not_taken: "bg-amber-500/15 text-amber-300",
  never_reached: "bg-zinc-700/40 text-zinc-400",
};

const REASON_LABEL = {
  not_taken: "not taken",
  never_reached: "never reached",
};

const SORTS = [
  { key: "step_order", label: "Step order" },
  { key: "decided_at", label: "When decided" },
  { key: "name", label: "Name" },
];

const Filter = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
      active ? "bg-violet-500/20 text-violet-200" : "text-gray-500 hover:text-gray-300"
    }`}
  >
    {children}
  </button>
);

/**
 * Por qué este paso no corrió, dicho sin adornos.
 *
 * De `not_taken` sabemos qué condición lo descartó, no a qué rama se fue —
 * eso no se registra, así que no se afirma.
 */
const explain = (item) =>
  item.reason === "not_taken"
    ? `The condition “${item.decided_by || "?"}” went down another branch, so this step was left out.`
    : "The run ended without ever reaching this step.";

const DetailPanel = ({ item, onClose }) => {
  if (!item) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-[11px] text-gray-600">
        Pick a step to see why it did not run.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between border-b border-white/10 px-4 py-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5">
            <StepIcon step={{ data: { step_type: item.type } }} size={16} />
          </span>
          <div className="min-w-0">
            <h4 className="truncate text-sm font-medium text-white">{item.name}</h4>
            <p className="truncate text-[10px] text-gray-500">{item.type}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-500 transition-colors hover:bg-white/5 hover:text-white"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-auto px-4 py-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Reason</div>
          <span
            className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
              REASON_BADGE[item.reason] || "bg-zinc-700/40 text-zinc-400"
            }`}
          >
            {REASON_LABEL[item.reason] || item.reason}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 text-xs text-gray-200">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Step order</div>
            <div className="mt-1">#{item.step_order}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Decided by</div>
            <div className="mt-1">{item.decided_by || "—"}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Decided at</div>
            <div className="mt-1">{item.decided_at ? formatTime(item.decided_at) : "—"}</div>
          </div>
        </div>

        <p className="rounded-lg border border-white/10 bg-black/30 p-2.5 text-[11px] leading-relaxed text-gray-400">
          {explain(item)}
        </p>
      </div>
    </div>
  );
};

export default function NotExecutedView({ notExecuted }) {
  const [query, setQuery] = useState("");
  const [reason, setReason] = useState("all");
  const [sort, setSort] = useState("step_order");
  const [selected, setSelected] = useState(null);

  const counts = useMemo(() => countByReason(notExecuted), [notExecuted]);
  const rows = useMemo(
    () => prepareNotExecuted(notExecuted, { query, reason, sort }),
    [notExecuted, query, reason, sort]
  );

  if (counts.all === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-xs text-gray-500">
        Every step of the graph ran at least once.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <p className="text-[11px] text-gray-500">
          These steps were left out by a condition, or the run ended before reaching them. They
          have no duration — a decision is an instant, not an interval.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[12rem] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-600" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name, type or condition…"
              className="w-full rounded-lg border border-white/10 bg-black/30 py-1.5 pl-8 pr-3 text-xs text-gray-200 placeholder:text-gray-600 focus:border-violet-500/40 focus:outline-none"
            />
          </div>

          <div className="flex items-center gap-1 rounded-lg border border-white/10 p-0.5">
            <Filter active={reason === "all"} onClick={() => setReason("all")}>
              All {counts.all}
            </Filter>
            <Filter active={reason === "not_taken"} onClick={() => setReason("not_taken")}>
              Not taken {counts.not_taken}
            </Filter>
            <Filter active={reason === "never_reached"} onClick={() => setReason("never_reached")}>
              Never reached {counts.never_reached}
            </Filter>
          </div>

          <select
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5 text-[11px] text-gray-300 focus:border-violet-500/40 focus:outline-none"
          >
            {SORTS.map((option) => (
              <option key={option.key} value={option.key} className="bg-zinc-900">
                Sort by {option.label.toLowerCase()}
              </option>
            ))}
          </select>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-white/10 bg-zinc-900/40">
          <div className="flex items-center gap-3 border-b border-white/10 bg-black/20 px-3 py-2 text-[9px] font-semibold uppercase tracking-wider text-gray-600">
            <span className="min-w-0 flex-1">step</span>
            <span className="w-28 shrink-0">reason</span>
            <span className="w-28 shrink-0">decided by</span>
            <span className="w-20 shrink-0 text-right">decided at</span>
            <span className="w-12 shrink-0 text-right">order</span>
          </div>

          <div className="min-h-0 flex-1 divide-y divide-white/5 overflow-auto">
            {rows.length === 0 && (
              <div className="flex h-32 items-center justify-center text-xs text-gray-500">
                No step matches that.
              </div>
            )}
            {rows.map((item) => (
              <button
                key={item.step_id}
                type="button"
                onClick={() => setSelected(item)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                  selected?.step_id === item.step_id
                    ? "bg-violet-500/10 ring-1 ring-inset ring-violet-500/40"
                    : "hover:bg-white/5"
                }`}
              >
                <span className="flex min-w-0 flex-1 items-center gap-2.5">
                  <StepIcon step={{ data: { step_type: item.type } }} size={14} />
                  <span className="min-w-0">
                    <span className="block truncate text-xs text-gray-100">{item.name}</span>
                    <span className="block truncate text-[10px] text-gray-500">{item.type}</span>
                  </span>
                </span>
                <span className="w-28 shrink-0">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${
                      REASON_BADGE[item.reason] || "bg-zinc-700/40 text-zinc-400"
                    }`}
                  >
                    {REASON_LABEL[item.reason] || item.reason}
                  </span>
                </span>
                <span className="w-28 shrink-0 truncate text-[11px] text-gray-300">
                  {item.decided_by || <span className="text-gray-700">—</span>}
                </span>
                <span className="w-20 shrink-0 text-right text-[10px] text-gray-500">
                  {item.decided_at ? formatTime(item.decided_at) : "—"}
                </span>
                <span className="w-12 shrink-0 text-right font-mono text-[10px] text-gray-600">
                  #{item.step_order}
                </span>
              </button>
            ))}
          </div>

          <div className="border-t border-white/10 px-3 py-1.5 text-[10px] text-gray-600">
            {rows.length} of {counts.all} steps that never ran
          </div>
        </div>
      </div>

      <div className="w-80 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-zinc-900/50">
        <DetailPanel item={selected} onClose={() => setSelected(null)} />
      </div>
    </div>
  );
}
